// Every figure is plain multiplication of the published constants below.
export const SRC = {
  eurostatMeat: {
    label: "Eurostat, Agricultural production — livestock and meat (2024)",
    url: "https://ec.europa.eu/eurostat/statistics-explained/index.php?title=Agricultural_production_-_livestock_and_meat",
  },
  faoMeat: {
    label: "FAO, Meat Market Review — 76.6 Mt bovine meat, 2023",
    url: "https://openknowledge.fao.org/server/api/core/bitstreams/ae4eb1ec-613d-478c-8361-c9bdba1df559/content",
  },
  meatLoss: {
    label: "Karwowska, Łaba & Szczepański, Sustainability 13(11):6227, 2021 — 23 % of EU meat",
    url: "https://doi.org/10.3390/su13116227",
  },
  faoLoss: {
    label: "FAO, Global food losses and food waste, 2011 — 20 % of meat worldwide",
    url: "https://www.fao.org/4/mb060e/mb060e.pdf",
  },
  ec: {
    label: "European Commission, Market study on date marking, 2018 — up to 10 % of food waste",
    url: "https://food.ec.europa.eu/system/files/2018-07/fw_lib_srp_date-marking.pdf",
  },
  yields: {
    label: "FAOSTAT via Our World in Data, cattle meat yield per animal (2024)",
    url: "https://ourworldindata.org/grapher/cattle-meat-yields-hectograms-100-grams-per-animal",
  },
  poore: {
    label: "Poore & Nemecek, Science 360(6392):987, 2018 — beef from beef herds, global mean",
    url: "https://ourworldindata.org/environmental-impacts-of-food",
  },
  epa: {
    label: "US EPA, Greenhouse gas emissions from a typical passenger vehicle — 4.6 t CO₂ a year",
    url: "https://www.epa.gov/greenvehicles/greenhouse-gas-emissions-typical-passenger-vehicle",
  },
  worldBank: {
    label: "World Bank, Land area (sq. km), 2023",
    url: "https://data.worldbank.org/indicator/AG.LND.TOTL.K2",
  },
  who: {
    label: "WHO, Domestic water quantity, service level and health — 50 L per person per day",
    url: "https://iris.who.int/server/api/core/bitstreams/ca623971-c9ee-4a72-9f60-f2a389ffb5c7/content",
  },
  eurostatPop: {
    label: "Eurostat, EU population 449.2 million on 1 January 2024",
    url: "https://ec.europa.eu/eurostat/web/products-eurostat-news/w/ddn-20240711-1",
  },
  unPop: {
    label: "UN World Population Prospects 2024 — 8.2 billion people",
    url: "https://population.un.org/wpp/",
  },
  unfccc: {
    label: "UNFCCC national inventories 2024 — total GHG excl. LULUCF, 2022",
    url: "https://unfccc.int/ghg-inventories-annex-i-parties/2024",
  },
  acea: {
    label: "ACEA, Vehicles on European roads 2026 — passenger cars in use, 2024",
    url: "https://www.acea.auto/files/ACEA_Report-%E2%80%93-Vehicles_on_European_roads_2026.pdf",
  },
  dft: {
    label: "UK DfT, Vehicle licensing statistics 2024 — 33.97 m licensed cars",
    url: "https://www.gov.uk/government/statistics/vehicle-licensing-statistics-2024/vehicle-licensing-statistics-united-kingdom-2024",
  },
};

export const FACTORS = {
  ghgKgPerKg: 99.48, // Poore & Nemecek 2018
  landM2PerKg: 326.21, // Poore & Nemecek 2018, one year of land use
  waterLPerKg: 1451.2, // Poore & Nemecek 2018, freshwater withdrawals
  carTPerYear: 4.6, // US EPA
  poolL: 2_500_000, // World Aquatics 50 × 25 × 2 m
  whoLPerDay: 50, // WHO intermediate access
  portionKg: 0.2, // stated assumption
};

export const REGIONS = {
  eu: {
    id: "eu",
    name: "EU",
    scope: "EU beef, per year",
    productionT: 6_600_000,
    productionLabel: "6.6 m t",
    productionSrc: SRC.eurostatMeat,
    lossShare: 0.23,
    lossSrc: SRC.meatLoss,
    dateShare: 0.1,
    dateAssumed: false,
    kgPerHead: 290,
    population: 449_200_000,
    populationLabel: "449 million EU residents",
    popSrc: SRC.eurostatPop,
    country: { name: "Slovakia", km2: 48_080 },
    ghgCountry: { name: "Estonia", mt: 13.95 },
    carCountry: { name: "Denmark", cars: 2_720_410 },
    people: "EU residents",
  },
  global: {
    id: "global",
    name: "Global",
    scope: "World beef, per year",
    productionT: 76_600_000,
    productionLabel: "76.6 m t",
    productionSrc: SRC.faoMeat,
    lossShare: 0.2,
    lossSrc: SRC.faoLoss,
    dateShare: 0.1,
    dateAssumed: true,
    kgPerHead: 229,
    population: 8_200_000_000,
    populationLabel: "8.2 billion people on Earth",
    popSrc: SRC.unPop,
    country: { name: "Spain", km2: 499_697 },
    ghgCountry: { name: "the Netherlands", mt: 153 },
    carCountry: { name: "the UK", cars: 33_967_000 },
    people: "people on Earth",
  },
};

export const WORLD_PRODUCTION_T = REGIONS.global.productionT;

// Pick a power of ten so a grid holds roughly 15–150 glyphs.
const niceUnit = (v) => 10 ** Math.ceil(Math.log10(v / 100));

export const compute = (r) => {
  const lostT = r.productionT * r.lossShare;
  const basisT = lostT * r.dateShare;
  const kg = basisT * 1000;
  const co2eMt = (kg * FACTORS.ghgKgPerKg) / 1e9;
  const cars = (co2eMt * 1e6) / FACTORS.carTPerYear;
  const hectares = (kg * FACTORS.landM2PerKg) / 10_000;
  const litres = kg * FACTORS.waterLPerKg;
  const cattle = kg / r.kgPerHead;
  const portions = kg / FACTORS.portionKg;
  const pools = litres / FACTORS.poolL;
  const peopleWater = litres / (FACTORS.whoLPerDay * 365);
  return {
    lostT,
    basisT,
    kg,
    shareOfWorld: r.productionT / WORLD_PRODUCTION_T,
    oneIn: Math.round(1 / (r.lossShare * r.dateShare)),
    cattle,
    cattleUnit: niceUnit(cattle),
    co2eMt,
    co2eUnit: niceUnit(co2eMt),
    cars,
    carsUnit: niceUnit(cars),
    hectares,
    landUnitMha: niceUnit(hectares / 1e6),
    countryShare: (hectares / 100) / r.country.km2,
    litres,
    pools,
    poolsUnit: niceUnit(pools),
    peopleWater,
    peopleUnit: niceUnit(peopleWater),
    portions,
    portionsUnit: niceUnit(portions),
    portionsPerPerson: portions / r.population,
  };
};

const nf = (d) => new Intl.NumberFormat("en-GB", { minimumFractionDigits: d, maximumFractionDigits: d });
export const fmt = (n, d = 0) => nf(d).format(n);

// 12,000 → "12,000" · 1,500,000 → "1.5 million" · 2.2e9 → "2.2 billion"
export const fmtBig = (n) => {
  if (n >= 1e9) return `${fmt(n / 1e9, n >= 1e10 ? 0 : 1)} billion`;
  if (n >= 1e6) return `${fmt(n / 1e6, n >= 1e7 ? 0 : 1)} million`;
  return fmt(n);
};
